import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { MondayService } from "../../services/monday/monday-service-ts";
import { tokenStore } from "../security/tokenStore";

const mondayService = new MondayService();

interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

const simbApi = axios.create({
  baseURL: `${process.env.REACT_APP_URI}`,
  timeout: 60000,
});

const refreshSessionToken = async (): Promise<string | null> => {
  const monday = await mondayService.getMondayServerSdkForUser();
  const response = await monday.get("sessionToken");
  const token = response.data as string;

  if (token) {
    tokenStore.setToken(token);
  }
  return token;
};

const getValidToken = async (): Promise<string | null> => {
  const token = tokenStore.getToken();

  if (tokenStore.isTokenExpired(token)) {
    return refreshSessionToken();
  }

  return token;
};

simbApi.interceptors.request.use(
  async (config) => {
    const token = await getValidToken();
    if (token) {
      config.headers.Authorization = token;
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

simbApi.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const token = await refreshSessionToken();
        if (token) {
          originalRequest.headers.Authorization = token;
        }
        return simbApi(originalRequest);
      } catch (refreshError) {
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

export default simbApi;
